import React, { type FC, useEffect, useMemo } from 'react';
import UFOCustomData from '@atlaskit/react-ufo/custom-data';
import { useExperienceStart } from '@atlassian/jira-experience-tracker/src/ui/experience-start/index.tsx';
import { useExperienceSuccess } from '@atlassian/jira-experience-tracker/src/ui/experience-success/index.tsx';
import { useCurrentRoute } from '@atlassian/jira-platform-router-utils/src/index.tsx';
import { useAnalyticsEvents, fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import { usePageId } from '@atlassian/jira-spa-state-controller/src/components/use-page-id/index.tsx';
import { useEffectWithoutConcurrency } from '@atlassian/jira-react-strict-mode/src/useEffectWithoutConcurrency.tsx';

export const useSidebarExperience = () => {
	const pageId = usePageId();

	return useMemo(
		() => ({
			experience: 'viewSidebarNavigation',
			experienceId: pageId,
			analyticsSource: 'Sidebar',
			attributes: {
				sidebar: 'v4',
			},
		}),
		[pageId],
	);
};

export const SidebarStart: FC = () => {
	const route = useCurrentRoute();
	const { experience, experienceId, analyticsSource } = useSidebarExperience();
	const onStartExperience = useExperienceStart({ experience, experienceId, analyticsSource });

	useEffect(() => {
		if (route.isRedirect) {
			return;
		}

		onStartExperience();
	}, [onStartExperience, route]);

	return null;
};

export const SidebarReady: FC = () => {
	const route = useCurrentRoute();
	const { experience, attributes } = useSidebarExperience();
	const onSuccessfulExperience = useExperienceSuccess({ experience, attributes });
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const routeName = route.name ?? route.group ?? 'UNKNOWN';
	const doNotRunEffects = route.isRedirect || __SERVER__;
	const sidebarId = route.navigation?.sidebarId ?? 'default';

	useEffectWithoutConcurrency(() => {
		if (doNotRunEffects) {
			return;
		}

		const event = createAnalyticsEvent({
			action: 'ready',
			actionSubject: 'sidebar',
			meta: { teamName: 'endeavour' },
		});

		fireUIAnalytics(event, 'Sidebar Ready', { route: routeName });
	}, [createAnalyticsEvent, doNotRunEffects, routeName]);

	useEffectWithoutConcurrency(() => {
		if (doNotRunEffects) {
			return;
		}

		onSuccessfulExperience();
	}, [doNotRunEffects, onSuccessfulExperience, route]);

	const customData = useMemo(
		() => ({
			sidebarType: sidebarId,
		}),
		[sidebarId],
	);

	return <UFOCustomData data={customData} />;
};
